import { useFormik } from 'formik';
import { AlertCircle, IndianRupee } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { DrawerSection } from '@/components/drawer/DrawerSection';
import { FieldError } from '@/components/form/FieldError';
import { ALL_PAYMENT_TYPES, PaymentTypeField } from '@/components/form/PaymentTypeField';
import { PmsBalanceNote } from '@/components/form/PmsBalanceNote';
import { StatusBadge } from '@/components/StatusBadge';
import { getApiErrorMessage } from '@/api/errorHandler';
import { LoanPaymentRequestMapper } from '@/api/mapper/loan';
import { createLoanPayment } from '@/api/service/loan';
import { PAID_BY_PMS, type PaymentType } from '@/api/type/payment';
import type { Loan } from '@/types';
import { formatCurrency } from '@/lib/payments';
import { formatMonthKey } from '@/lib/months';
import { getLoanInterest, LOAN_MIN_PRINCIPAL, LOAN_RATE } from '@/lib/loans';
import { FIELD_CLASS, LABEL_CLASS, PRIMARY_BUTTON_CLASS, READONLY_FIELD_CLASS } from '@/lib/styles';

interface LoanPaymentFormProps {
  loan: Loan;
  memberName: string;
  /** The member's balance due, shown when Paid by PMS is picked. */
  balanceDue: number;
  onSaved: () => void;
}

interface LoanPaymentValues {
  principal: string;
  paymentType: PaymentType;
}

/** Records one loan repayment: principal chosen by the admin, interest worked out on the outstanding. */
export function LoanPaymentForm({ loan, memberName, balanceDue, onSaved }: LoanPaymentFormProps) {
  const outstanding = loan.outstanding;
  const minPrincipal = loan.nextPayment?.minPrincipal ?? Math.min(LOAN_MIN_PRINCIPAL, outstanding);
  const interest = loan.nextPayment?.interest ?? getLoanInterest(outstanding);
  const forMonth = loan.nextPayment?.forMonth ?? loan.nextDueMonth;

  const formik = useFormik<LoanPaymentValues>({
    initialValues: { principal: String(minPrincipal), paymentType: 'Cash' },
    validate: ({ principal }) => {
      const value = Number(principal);
      if (!principal) return { principal: 'Please enter principal amount' };
      if (value < minPrincipal) return { principal: `Minimum principal is ${formatCurrency(minPrincipal)}` };
      if (value > outstanding) return { principal: `Principal cannot be more than outstanding ${formatCurrency(outstanding)}` };
      return {};
    },
    onSubmit: async (values, { setStatus }) => {
      setStatus(undefined);
      try {
        await createLoanPayment(
          Number(loan.id),
          LoanPaymentRequestMapper({ principal: Number(values.principal), paymentType: values.paymentType })
        );
        onSaved();
      } catch (error) {
        setStatus(getApiErrorMessage(error));
      }
    },
  });

  const principal = Number(formik.values.principal) || 0;
  const isValid = !formik.errors.principal && principal > 0;
  const balanceAfter = outstanding - principal;

  return (
    <DrawerSection
      icon={IndianRupee}
      title="Loan Payment"
      description={`For ${formatMonthKey(forMonth)} · interest ${LOAN_RATE * 100}% per year on ${formatCurrency(outstanding)}`}
    >
      <form noValidate onSubmit={formik.handleSubmit} className="space-y-4">
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="space-y-2">
            <div className="flex min-h-5 items-center justify-between">
              <Label htmlFor="loan-principal" className={LABEL_CLASS}>
                Principal (₹) *
              </Label>
              <span className="text-xs text-gray-400">Min {formatCurrency(minPrincipal)}</span>
            </div>
            <Input
              id="loan-principal"
              inputMode="numeric"
              value={formik.values.principal}
              onChange={(e) => formik.setFieldValue('principal', e.target.value.replace(/\D/g, '').slice(0, 6))}
              className={FIELD_CLASS}
            />
            <FieldError message={formik.errors.principal} />
          </div>
          <div className="space-y-2">
            <Label className={LABEL_CLASS}>Interest (₹)</Label>
            <Input readOnly value={formatCurrency(interest)} className={READONLY_FIELD_CLASS} />
            <p className="text-xs text-gray-500">
              {formatCurrency(outstanding)} × {LOAN_RATE * 100}% ÷ 12
            </p>
          </div>
        </div>

        <PaymentTypeField
          id="loan-payment-type"
          options={ALL_PAYMENT_TYPES}
          value={formik.values.paymentType}
          onChange={(value) => formik.setFieldValue('paymentType', value)}
        />
        {formik.values.paymentType === PAID_BY_PMS && (
          <PmsBalanceNote
            memberName={memberName}
            amount={principal >= minPrincipal && principal <= outstanding ? principal + interest : null}
            currentBalance={balanceDue}
          />
        )}

        <dl className="divide-y divide-gray-100 border-t border-gray-100">
          <div className="flex items-center justify-between gap-6 py-3">
            <dt className="text-sm text-gray-500">Total to Collect</dt>
            <dd className="text-base font-semibold tabular-nums text-teal-800">
              {isValid ? formatCurrency(principal + interest) : '--'}
            </dd>
          </div>
          <div className="flex items-center justify-between gap-6 py-3">
            <dt className="text-sm text-gray-500">Balance After Payment</dt>
            <dd className="text-sm font-medium tabular-nums text-gray-900">
              {!isValid ? '--' : balanceAfter === 0 ? <StatusBadge status="closed" /> : formatCurrency(balanceAfter)}
            </dd>
          </div>
        </dl>

        {formik.status && (
          <div className="flex items-start gap-2.5 rounded-lg border border-red-200 bg-red-50 px-3.5 py-2.5 text-sm text-red-700">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{formik.status}</span>
          </div>
        )}

        <div className="flex justify-end">
          <Button type="submit" disabled={formik.isSubmitting} className={`min-w-36 ${PRIMARY_BUTTON_CLASS}`}>
            {formik.isSubmitting ? 'Saving...' : 'Add Loan Payment'}
          </Button>
        </div>
      </form>
    </DrawerSection>
  );
}
